import api from '../../lib/api';
import { formatDateToLocal, getEvidenceDisplayName } from '@safetyhub/shared';
import { ApprovalSignModal } from '../../components/approval/ApprovalSignModal';
import {
  ExternalSignatureShell,
  type ExternalApiError,
  type LoaderResult,
} from '../../components/external-signature/ExternalSignatureShell';
import type { ApprovalInfoData } from '../../utils/reportNormalizer';
import { tbmUnfinishedRoundNotice } from '../../lib/tbmConductorSignature';
import { APPROVAL_ERROR_TITLES } from './approvalErrorTitles';

interface ApprovalInfo extends ApprovalInfoData {}
type ActionResult = { action: 'APPROVE' | 'REJECT' };

async function loadApprovalInfo(token: string): Promise<LoaderResult<ApprovalInfo>> {
  const response = await api.get(`/approval/${token}`, { skipAuth: true } as any);
  if (response.data.success) {
    return { ok: true, info: response.data.data as ApprovalInfo };
  }
  return { ok: false, error: response.data.error as ExternalApiError };
}

export default function ExternalApprovalPage() {
  return (
    <ExternalSignatureShell<ApprovalInfo, ActionResult>
      loader={loadApprovalInfo}
      extractReport={(info) => info.report}
      toolbar={{
        badgeLabel: (info) => `${info.signature.approvalLevel}단계 결재`,
        badgeTone: 'brand',
        title: (info) => info.report.title,
        subtitle: (info) =>
          `${info.report.site.name} | ${getEvidenceDisplayName(info.report.authorSignature?.signerName ?? null, { name: info.report.author.name })} | ${formatDateToLocal(new Date(info.report.createdAt))}`,
        actionLabel: '결재하기',
        actionButtonClassName: 'bg-brand-600 hover:bg-brand-700',
        /**
         * 미완료로 박제된 교육 회차 고지(2026-09-09) — **이 페이지에만** 붙인다.
         *
         * 결재자는 「진행된 내용」을 보고 승인 여부를 정하는데, 미완료 회차는 남되 담당자 확인
         * 서명란이 영영 비므로 그 사실을 모르면 *다섯 명이 서명한 2일차*를 못 본 채 승인하게
         * 된다. 종전 이 화면에는 그 축이 **한 글자도 없었다**(사유 참조 0건).
         *
         * 재료는 문서 상태와 회차 상태 둘뿐이고 `isEmpty`는 필요 없다
         * (`external-report-view.helper.ts`가 그 필드를 일부러 빼는 사유가 그대로 유지된다).
         * 판정·문구는 `lib/tbmConductorSignature` 한 벌 — 인증 상세 chrome과 같은 말을 한다.
         *
         * ⚠️ 회차 상태는 `educationSessions`가 아니라 **`educationSessionStatuses`**에서 온다 —
         * 앞엣것은 레거시 `type === 'TBM'` 게이트 안이라 COMPOSED 실문서에는 안 실린다(F-11).
         */
        notice: (info) =>
          tbmUnfinishedRoundNotice({
            documentStatus: info.report.status,
            sessionStatuses: info.report.educationSessionStatuses,
          }),
      }}
      loadingMessage="결재 정보를 불러오는 중..."
      processingMessage="결재를 처리하고 있습니다..."
      // 제목표는 `approvalErrorTitles.ts` 한 곳 — 서버 wire 코드와의 대조는 그 spec이 기계로 지킨다.
      errorTitleMap={APPROVAL_ERROR_TITLES}
      renderModal={({ info, token, isOpen, onClose, onSubmitted, setProcessing, setError }) => (
        <ApprovalSignModal
          isOpen={isOpen}
          onClose={onClose}
          approvalLevel={info.signature.approvalLevel}
          defaultSignerName={info.signature.signerName || ''}
          defaultSignerTitle={info.signature.signerTitle || ''}
          // 판정은 서버가 한다 — 발송을 실제로 끊는 것과 같은 술어라야 안내가 거짓이 되지 않는다.
          relayedContactName={
            // ⚠️ `??`가 아니라 `||`다 — 이름이 **빈 문자열**이면 `??`는 그걸 그대로 통과시키고,
            // 받는 쪽은 falsy를 "릴레이 아님"으로 읽어 **경고가 통째로 사라진다**. 릴레이 여부를
            // 정하는 축은 `relaysComment`(서버 판정)이므로, 이름이 비었다고 안내를 끄면 안 된다.
            info.delegation?.relaysComment ? info.delegation.contactName || '담당자' : undefined
          }
          onSubmit={async (params) => {
            setProcessing();
            try {
              const response = await api.post(
                `/approval/${token}/process`,
                {
                  action: params.action,
                  signerName: params.signerName,
                  signerTitle: params.signerTitle,
                  comment: params.comment,
                  signatureMethod: 'DRAWN',
                  signatureData: params.signatureData,
                },
                { skipAuth: true } as any,
              );
              if (response.data.success) {
                onSubmitted({ action: params.action });
              } else {
                setError(
                  response.data.error ?? {
                    code: 'PROCESS_ERROR',
                    message: '처리 중 오류가 발생했습니다.',
                  },
                );
              }
            } catch {
              setError({
                code: 'NETWORK_ERROR',
                message: '처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
              });
            }
          }}
        />
      )}
      renderSuccess={(result) => (
        <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-card shadow-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <span className="h-8 w-8 text-green-600 text-2xl">&#10003;</span>
            </div>
            <h1 className="text-xl font-semibold text-ink-900 mb-2">
              {result?.action === 'APPROVE'
                ? '결재가 승인되었습니다'
                : '결재가 반려되었습니다'}
            </h1>
            <p className="text-ink-600 mb-6">
              결재 처리가 완료되었습니다. 이 창을 닫아도 됩니다.
            </p>
          </div>
        </div>
      )}
    />
  );
}
